/**
 * The player profile: XP, levels, Credits, the Rocket Pass track, and how all
 * of it survives a reload.
 *
 * ===========================================================================
 * THE SANITIZE RULE, COPIED FROM ./settings AND NOT SHARED WITH IT
 * ===========================================================================
 *
 * `sanitizeProfile` is TOTAL: any input at all — undefined, a string, an array,
 * an object with the right keys and the wrong types — yields a complete, valid
 * `PlayerProfile`. localStorage is trivially writable from devtools and can
 * hold a half-finished write from a crashed tab, and a profile that will not
 * parse must cost the player their items, not their ability to start the game.
 *
 * The profile lives under its OWN key with its OWN version, deliberately
 * separate from `GameSettings`. ./cosmetics says why: a cosmetics migration
 * must not be able to cost somebody their key bindings. That means the two
 * files look similar and share nothing, which is the intended trade.
 *
 * ===========================================================================
 * CREDITS ARE EARNED
 * ===========================================================================
 *
 * `awardMatch` here, and the challenge and pass claims in ./challenges and
 * ./rocketpass, are the only things in this codebase that ever increase
 * `wallet.credits` — and every one of them is driven by a match that was
 * played. There is no purchase path and none is to be added; see the header of
 * ./cosmetics.
 *
 * ===========================================================================
 * THIS FILE OWNS THE XP CURVE AND THE TIER-POINT CONVERSION
 * ===========================================================================
 *
 * `levelForXp` is the only thing that decides when a level happens, and
 * `applyTierPoints` is the only writer of `passTier`. ./rocketpass wraps both
 * rather than reimplementing either, and ./challenges goes through that
 * wrapper. Two files deciding when you level up is how a player gains a level
 * and no tier.
 *
 * ===========================================================================
 * XP COMES FROM EVENTS THE SIMULATION ACTUALLY REPORTS
 * ===========================================================================
 *
 * Every term in `xpForMatch` reads a field of `MatchStats`, and every field of
 * `MatchStats` is something `WorldStepEvents` (src/sim/world.ts) or `GoalEvent`
 * (./match) produces: goals and their flavours, assists, saves, shots, demos,
 * touches, boost pickups, seconds played. Nothing here is awarded for a thing
 * the game cannot see happen.
 */

import type { Rng } from '../physics/rng';
import { legacyKeyFor, readMigrating } from './storage-migration';
import {
  CERTIFICATIONS,
  COSMETIC_SLOTS,
  PAINT_COLORS,
  PROFILE_VERSION,
  TIER_POINTS_PER_LEVEL,
  TIER_POINTS_PER_TIER,
  type Blueprint,
  type Certification,
  type CosmeticSlot,
  type InventoryItem,
  type MatchStats,
  type PaintColor,
  type PlayerProfile,
  type PlayerProgress,
  type Preset,
} from './cosmetics';
import { blueprintCost, rollDrop } from './drops';
import {
  advanceCertifications,
  defaultLoadout,
  defaultPreset,
  grantDrops,
  MAX_PRESETS,
} from './inventory';
import { itemById } from './items';
import { emptyMatchStats, mergeMatchStats } from './match-stats';

// ---------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------

/**
 * Aliases, not definitions. ./match-stats is the file that decides what a save
 * is and what an empty stat block looks like, and its header says so; a second
 * zeroed literal here would be a second thing to update when `MatchStats` grows
 * a field. The names are the ones the cosmetics API was specified with.
 */
export const emptyStats = emptyMatchStats;
export const addStats = mergeMatchStats;

const STAT_KEYS = Object.keys(emptyStats()) as (keyof MatchStats)[];

// ---------------------------------------------------------------------------
// The XP curve
// ---------------------------------------------------------------------------

/**
 * Cost of the level n -> n+1 step: `XP_LEVEL_BASE + XP_LEVEL_STEP * (n - 1)`,
 * flattening at `XP_LEVEL_CAP`.
 *
 * A ramp that flattens is the shape Rocket League uses and it is the right one:
 * the early levels have to arrive inside the first session or the pass looks
 * unreachable, and the late ones have to stop growing or a level becomes a
 * week's work. The numbers below are ours — the real game's per-level XP is not
 * published as a table — and they are chosen against `xpForMatch`: a decent
 * five-minute win pays about 1,600 XP, so level 2 is a match and a half and the
 * flat region is three matches a level.
 */
export const XP_LEVEL_BASE = 1000;
export const XP_LEVEL_STEP = 200;
export const XP_LEVEL_CAP = 5000;

/** The last level whose cost is still on the ramp. */
const RAMP_LEVELS = Math.floor((XP_LEVEL_CAP - XP_LEVEL_BASE) / XP_LEVEL_STEP);

/** Total XP at the end of the ramp, i.e. `xpForLevel(RAMP_LEVELS + 1)`. */
const RAMP_TOTAL =
  XP_LEVEL_BASE * RAMP_LEVELS + (XP_LEVEL_STEP * RAMP_LEVELS * (RAMP_LEVELS - 1)) / 2;

/** XP to get from `level` to `level + 1`. */
export const xpForLevelStep = (level: number): number =>
  level < 1 ? 0 : Math.min(XP_LEVEL_BASE + XP_LEVEL_STEP * (level - 1), XP_LEVEL_CAP);

/**
 * Cumulative XP required to BE at `level`. Level 1 costs nothing — everybody
 * starts there. Closed form rather than a loop so that a level in the thousands
 * costs the same to compute as level 2.
 */
export const xpForLevel = (level: number): number => {
  const n = Math.max(1, Math.floor(level));
  if (n <= RAMP_LEVELS + 1) {
    const steps = n - 1;
    return XP_LEVEL_BASE * steps + (XP_LEVEL_STEP * steps * (steps - 1)) / 2;
  }
  return RAMP_TOTAL + XP_LEVEL_CAP * (n - (RAMP_LEVELS + 1));
};

/** The level a total XP figure buys. Exact inverse of `xpForLevel`. */
export const levelForXp = (xp: number): number => {
  const total = Math.max(0, Math.floor(xp));
  if (total >= RAMP_TOTAL) return RAMP_LEVELS + 1 + Math.floor((total - RAMP_TOTAL) / XP_LEVEL_CAP);
  // At most RAMP_LEVELS iterations, so the loop is bounded by a constant.
  let level = 1;
  let spent = 0;
  while (level <= RAMP_LEVELS) {
    const step = xpForLevelStep(level);
    if (spent + step > total) break;
    spent += step;
    level++;
  }
  return level;
};

/** Progress through the current level, 0..1, for the XP bar. */
export const levelProgress = (xp: number): number => {
  const level = levelForXp(xp);
  const start = xpForLevel(level);
  const step = xpForLevelStep(level);
  return step <= 0 ? 0 : Math.min(1, Math.max(0, (xp - start) / step));
};

// ---------------------------------------------------------------------------
// Match XP and Credits
// ---------------------------------------------------------------------------

/**
 * What a match pays, term by term. Every key names a `MatchStats` field except
 * the two flat bonuses, which is what makes `xpForMatch` a dot product and lets
 * the end-of-match screen show its own working.
 */
export const XP_RATES = Object.freeze({
  /** Per second of match time — 600 XP for a full five minutes. */
  secondsPlayed: 2,
  goals: 100,
  assists: 50,
  saves: 50,
  shots: 20,
  demolitions: 25,
  /** Ball touches, which is what keeps a player who never scores earning. */
  touches: 1,
});

export const XP_MVP_BONUS = 200;
export const XP_WIN_BONUS = 300;

/** The XP a match summary is worth, itemised for the results screen. */
export const xpBreakdown = (
  stats: MatchStats,
  won: boolean
): readonly { label: string; xp: number }[] => {
  const rows: { label: string; xp: number }[] = [
    { label: 'Time Played', xp: Math.round(stats.secondsPlayed * XP_RATES.secondsPlayed) },
    { label: 'Goals', xp: stats.goals * XP_RATES.goals },
    { label: 'Assists', xp: stats.assists * XP_RATES.assists },
    { label: 'Saves', xp: stats.saves * XP_RATES.saves },
    { label: 'Shots', xp: stats.shots * XP_RATES.shots },
    { label: 'Demolitions', xp: stats.demolitions * XP_RATES.demolitions },
    { label: 'Ball Touches', xp: stats.touches * XP_RATES.touches },
    { label: 'MVP', xp: stats.mvps * XP_MVP_BONUS },
    { label: 'Win', xp: won ? XP_WIN_BONUS : 0 },
  ];
  return rows.filter(r => r.xp > 0);
};

export const xpForMatch = (stats: MatchStats, won: boolean): number =>
  xpBreakdown(stats, won).reduce((sum, r) => sum + r.xp, 0);

/**
 * Credits per match. Small and flat on purpose: `PASS_PREMIUM_COST` is 1,000,
 * so the premium track is about thirty matches of play and nothing else can
 * shorten that — there is no purchase path (see the header).
 */
export const CREDITS_PER_MATCH = 10;
export const CREDITS_PER_GOAL = 5;
export const CREDITS_WIN_BONUS = 15;
/** Levelling pays too, which is what makes a long session worth playing out. */
export const CREDITS_PER_LEVEL = 25;

export const creditsForMatch = (stats: MatchStats, won: boolean): number =>
  CREDITS_PER_MATCH + stats.goals * CREDITS_PER_GOAL + (won ? CREDITS_WIN_BONUS : 0);

/** Items dropped for each level gained. */
export const DROPS_PER_LEVEL = 1;

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

/** Stable across versions; the version lives inside the payload. */
export const PROFILE_STORAGE_KEY = 'vroomball.profile';

export const DEFAULT_PROFILE_NAME = 'Player';

/**
 * What a new player starts with. Deliberately not empty: a garage whose every
 * slot is "None" cannot demonstrate what a garage is for, so the stock body,
 * gloss finish, stock wheels, standard boost and the Rookie title are granted
 * and equipped. Everything else is earned.
 */
export const STARTER_ITEMS: readonly string[] = [
  'body-octane',
  'body-dominus',
  'pf-gloss',
  'wh-spoke5',
  'bo-standard',
  'ti-rookie',
  'ab-plain',
  'bn-plain',
  'ea-stock',
];

export const defaultProgress = (): PlayerProgress => ({
  xp: 0,
  level: 1,
  tierPoints: 0,
  passTier: 1,
  passPremium: false,
  stats: emptyStats(),
});

/** Which slot each starter item lands in. Bodies are handled separately. */
const STARTER_EQUIP: Readonly<Record<string, CosmeticSlot>> = {
  'pf-gloss': 'paintFinish',
  'wh-spoke5': 'wheels',
  'bo-standard': 'boost',
  'ti-rookie': 'title',
  'ab-plain': 'avatarBorder',
  'bn-plain': 'banner',
  'ea-stock': 'engineAudio',
};

type SlotMap = Record<string, string | null>;

/**
 * A fresh profile, identical on every machine and every boot: the instance ids
 * and `acquiredAt` values are a fixed sequence rather than a clock reading, so
 * two players who have never played have byte-identical saves.
 */
export const defaultProfile = (): PlayerProfile => {
  const preset = defaultPreset();
  const profile: PlayerProfile = {
    version: PROFILE_VERSION,
    displayName: DEFAULT_PROFILE_NAME,
    wallet: { credits: 0 },
    progress: defaultProgress(),
    inventory: [],
    blueprints: [],
    presets: [preset],
    activePresetId: preset.id,
    challenges: [],
    lastShopDay: 0,
  };
  let n = 0;
  for (const itemId of STARTER_ITEMS) {
    if (itemById(itemId) === undefined) continue;
    n++;
    profile.inventory.push({ instanceId: `c${n}`, itemId, acquiredAt: n });
  }
  const instanceOf = (itemId: string): string | null =>
    profile.inventory.find(i => i.itemId === itemId)?.instanceId ?? null;

  for (const [itemId, slot] of Object.entries(STARTER_EQUIP)) {
    const id = instanceOf(itemId);
    if (id === null) continue;
    (preset.blue.slots as SlotMap)[slot] = id;
    (preset.orange.slots as SlotMap)[slot] = id;
  }
  // Blue drives the Octane and orange the Dominus, the same rule
  // `carBodyForTeam` in ./settings applies: both bodies are then always on
  // screen and the silhouette carries the same information the paint does.
  (preset.blue.slots as SlotMap).body = instanceOf('body-octane');
  (preset.orange.slots as SlotMap).body = instanceOf('body-dominus');
  return profile;
};

// ---------------------------------------------------------------------------
// Awarding
// ---------------------------------------------------------------------------

export interface MatchAward {
  profile: PlayerProfile;
  xpGained: number;
  levelsGained: number;
  drops: InventoryItem[];
  creditsGained: number;
  tierPointsGained: number;
  tiersGained: number;
}

/**
 * Everything a finished match does to a profile, in one call: lifetime stats,
 * XP, levels, Tier Points, pass tiers, Credits, certification counters and the
 * level-up drops. `rng` is threaded all the way through so the drops are a
 * function of the seed, which is the only way a match result is testable.
 *
 * `won` is passed separately from `stats.wins` on purpose: the caller owns what
 * goes into the lifetime counters, and the win BONUS is about this match.
 */
export const awardMatch = (
  p: PlayerProfile,
  stats: MatchStats,
  won: boolean,
  rng: Rng
): MatchAward => {
  const xpGained = xpForMatch(stats, won);
  const creditsGained = creditsForMatch(stats, won);

  const beforeLevel = levelForXp(p.progress.xp);
  const xp = Math.max(0, p.progress.xp) + xpGained;
  const afterLevel = levelForXp(xp);
  const levelsGained = Math.max(0, afterLevel - beforeLevel);

  // Certifications count what THIS match produced, on the items that were
  // equipped while it was played — so this runs before anything is granted.
  let next = advanceCertifications(p, stats);

  const rolls = [];
  for (let i = 0; i < levelsGained * DROPS_PER_LEVEL; i++) {
    const roll = rollDrop(rng);
    if (roll !== null) rolls.push(roll);
  }
  const granted = grantDrops(next, rolls);
  next = granted.profile;

  next.progress.stats = addStats(next.progress.stats, stats);
  next.progress.xp = xp;
  next.progress.level = afterLevel;
  next.wallet.credits += creditsGained + levelsGained * CREDITS_PER_LEVEL;

  const tierPointsGained = levelsGained * TIER_POINTS_PER_LEVEL;
  const tierBefore = next.progress.passTier;
  applyTierPoints(next.progress, tierPointsGained);

  return {
    profile: next,
    xpGained,
    levelsGained,
    drops: granted.granted,
    creditsGained: creditsGained + levelsGained * CREDITS_PER_LEVEL,
    tierPointsGained,
    tiersGained: next.progress.passTier - tierBefore,
  };
};

/**
 * Add Tier Points and convert them to tiers, in place.
 *
 * THE SINGLE WRITER OF `passTier`. ./rocketpass wraps this rather than
 * reimplementing it, and ./challenges goes through that wrapper, so "a tier is
 * exactly ten points" is a property of one function instead of one that four
 * call sites happen to agree on.
 *
 * The remainder stays in `tierPoints`, so two five-point challenges are one
 * tier and not zero. Negative and non-finite input is ignored rather than being
 * allowed to wind the pass backwards. `PASS_TIERS` is 70 and the tier is NOT
 * capped there — the real pass continues into Pro Tiers, so a player who keeps
 * playing keeps advancing.
 */
export const applyTierPoints = (progress: PlayerProgress, points: number): void => {
  if (!Number.isFinite(points) || points <= 0) return;
  const total = progress.tierPoints + Math.floor(points);
  progress.tierPoints = total % TIER_POINTS_PER_TIER;
  progress.passTier += Math.floor(total / TIER_POINTS_PER_TIER);
};

// ---------------------------------------------------------------------------
// Sanitizing
// ---------------------------------------------------------------------------

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const num = (v: unknown, fallback: number, min = 0, max = Number.MAX_SAFE_INTEGER): number =>
  typeof v === 'number' && Number.isFinite(v)
    ? Math.min(max, Math.max(min, Math.floor(v)))
    : fallback;

const str = (v: unknown, fallback: string, max = 64): string =>
  typeof v === 'string' && v.length > 0 ? v.replace(/\p{C}/gu, '').slice(0, max) : fallback;

const bool = (v: unknown, fallback: boolean): boolean => (typeof v === 'boolean' ? v : fallback);

const isPaint = (v: unknown): v is PaintColor =>
  typeof v === 'string' && (PAINT_COLORS as readonly string[]).includes(v);

const isCert = (v: unknown): v is Certification =>
  typeof v === 'string' && (CERTIFICATIONS as readonly string[]).includes(v);

const sanitizeStats = (v: unknown): MatchStats => {
  const out = emptyStats();
  if (!isRecord(v)) return out;
  for (const key of STAT_KEYS) out[key] = num((v as Record<string, unknown>)[key], 0);
  return out;
};

const sanitizeProgress = (v: unknown): PlayerProgress => {
  const base = defaultProgress();
  if (!isRecord(v)) return base;
  const xp = num(v.xp, 0);
  return {
    xp,
    // Recomputed from XP rather than trusted: the curve is the authority, and
    // a stored level is the one field a cheat would edit first.
    level: levelForXp(xp),
    tierPoints: num(v.tierPoints, 0, 0, TIER_POINTS_PER_TIER - 1),
    passTier: num(v.passTier, 1, 1),
    passPremium: bool(v.passPremium, false),
    stats: sanitizeStats(v.stats),
  };
};

/**
 * An inventory entry, or null if it is unusable. Unknown item ids are dropped
 * — a catalogue can shrink between builds, and an instance pointing at nothing
 * is an item that renders as nothing and can never be equipped.
 */
const sanitizeInventoryItem = (v: unknown, seen: Set<string>): InventoryItem | null => {
  if (!isRecord(v)) return null;
  const instanceId = str(v.instanceId, '');
  const itemId = str(v.itemId, '');
  if (instanceId === '' || itemId === '' || seen.has(instanceId)) return null;
  const def = itemById(itemId);
  if (def === undefined) return null;
  const paint = def.paintable && isPaint(v.paint) ? v.paint : undefined;
  const certification = def.certifiable && isCert(v.certification) ? v.certification : undefined;
  seen.add(instanceId);
  return {
    instanceId,
    itemId,
    ...(paint ? { paint } : {}),
    ...(certification ? { certification, certValue: num(v.certValue, 0) } : {}),
    acquiredAt: num(v.acquiredAt, 0),
  };
};

const sanitizeBlueprint = (v: unknown, seen: Set<string>): Blueprint | null => {
  if (!isRecord(v)) return null;
  const instanceId = str(v.instanceId, '');
  const itemId = str(v.itemId, '');
  if (instanceId === '' || itemId === '' || seen.has(instanceId)) return null;
  const def = itemById(itemId);
  if (def === undefined) return null;
  const paint = def.paintable && isPaint(v.paint) ? v.paint : undefined;
  seen.add(instanceId);
  return {
    instanceId,
    itemId,
    ...(paint ? { paint } : {}),
    cost: num(v.cost, blueprintCost(def, paint), 0),
    acquiredAt: num(v.acquiredAt, 0),
  };
};

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

const sanitizeLoadout = (v: unknown, team: 0 | 1) => {
  const base = defaultLoadout(team);
  if (!isRecord(v)) return base;
  const slots: Record<string, string | null> = {};
  if (isRecord(v.slots)) {
    for (const slot of COSMETIC_SLOTS) {
      const id = v.slots[slot];
      slots[slot] = typeof id === 'string' && id.length > 0 ? id : null;
    }
  }
  const primary = typeof v.primaryColor === 'string' && HEX_COLOR.test(v.primaryColor)
    ? v.primaryColor
    : base.primaryColor;
  const accent = typeof v.accentColor === 'string' && HEX_COLOR.test(v.accentColor)
    ? v.accentColor
    : base.accentColor;
  return { slots, primaryColor: primary, accentColor: accent };
};

const sanitizePreset = (v: unknown, index: number): Preset => {
  const fallback = defaultPreset(`preset-${index + 1}`, `Preset ${index + 1}`);
  if (!isRecord(v)) return fallback;
  return {
    id: str(v.id, fallback.id, 32),
    name: str(v.name, fallback.name, 32),
    blue: sanitizeLoadout(v.blue, 0),
    orange: sanitizeLoadout(v.orange, 1),
  };
};

const sanitizeChallenge = (v: unknown) => {
  if (!isRecord(v)) return null;
  const id = str(v.id, '');
  if (id === '') return null;
  return {
    id,
    value: num(v.value, 0),
    claimed: bool(v.claimed, false),
    expiresAt: num(v.expiresAt, 0),
  };
};

/**
 * Total: a complete, valid `PlayerProfile` for literally any input.
 *
 * The two rules worth stating outright. An equipped instance that is no longer
 * in the inventory is CLEARED, not kept — a dangling reference would otherwise
 * survive every future save. And presets are de-duplicated by id, because two
 * presets sharing an id makes "the active preset" ambiguous and every edit
 * would land on whichever one came first.
 */
export const sanitizeProfile = (value: unknown): PlayerProfile => {
  if (!isRecord(value)) return defaultProfile();

  const seenInstances = new Set<string>();
  const inventory = Array.isArray(value.inventory)
    ? value.inventory
        .map(v => sanitizeInventoryItem(v, seenInstances))
        .filter((v): v is InventoryItem => v !== null)
    : [];
  const blueprints = Array.isArray(value.blueprints)
    ? value.blueprints
        .map(v => sanitizeBlueprint(v, seenInstances))
        .filter(<T,>(v: T | null): v is T => v !== null)
    : [];

  const seenPresets = new Set<string>();
  const presets: Preset[] = [];
  if (Array.isArray(value.presets)) {
    for (const raw of value.presets.slice(0, MAX_PRESETS)) {
      const preset = sanitizePreset(raw, presets.length);
      if (seenPresets.has(preset.id)) continue;
      seenPresets.add(preset.id);
      presets.push(preset);
    }
  }
  if (presets.length === 0) presets.push(defaultPreset());

  // Drop equips that point at nothing, and equips whose item belongs to
  // another slot — both are reachable from a hand-edited payload.
  const owned = new Map(inventory.map(i => [i.instanceId, i]));
  for (const preset of presets) {
    for (const loadout of [preset.blue, preset.orange]) {
      const slots = loadout.slots as Record<string, string | null>;
      for (const slot of Object.keys(slots)) {
        const id = slots[slot];
        if (id === null) continue;
        const inv = id === undefined ? undefined : owned.get(id);
        const def = inv ? itemById(inv.itemId) : undefined;
        if (def === undefined || def.slot !== slot) slots[slot] = null;
      }
    }
  }

  const activePresetId = str(value.activePresetId, presets[0]?.id ?? 'preset-1', 32);

  return {
    version: PROFILE_VERSION,
    displayName: str(value.displayName, DEFAULT_PROFILE_NAME, 24),
    wallet: { credits: num(isRecord(value.wallet) ? value.wallet.credits : undefined, 0) },
    progress: sanitizeProgress(value.progress),
    inventory,
    blueprints,
    presets,
    activePresetId: seenPresets.has(activePresetId)
      ? activePresetId
      : (presets[0]?.id ?? 'preset-1'),
    challenges: Array.isArray(value.challenges)
      ? value.challenges
          .map(sanitizeChallenge)
          .filter(<T,>(v: T | null): v is T => v !== null)
      : [],
    lastShopDay: num(value.lastShopDay, 0),
  };
};

// ---------------------------------------------------------------------------
// Persistence
// ---------------------------------------------------------------------------

/** The slice of the Storage API used here; lets tests pass a plain object. */
export type ProfileStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * Resolved lazily, never at module scope: importing this file in Node, in a
 * worker or in a locked-down iframe must not touch `localStorage`.
 */
export const browserStorage = (): ProfileStorage | null => {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
};

export type ProfileLoadStatus = 'ok' | 'missing' | 'corrupt' | 'stale' | 'unavailable';

export interface ProfileLoadResult {
  profile: PlayerProfile;
  status: ProfileLoadStatus;
}

/**
 * Version handling, and the same rule ./settings states: a payload from another
 * schema version is dropped WHOLESALE rather than salvaged field by field. v1
 * is the first schema, so "older" can only mean hand-written or pre-release.
 *
 * TODO(schema): when v2 lands, add a `case 1:` that upgrades the v1 shape and
 * returns status 'ok'. `sanitizeProfile` still runs afterwards, so a migration
 * only has to move fields, not validate them.
 *
 * Note what this does NOT do: a field ADDED to v1 needs no version bump,
 * because `sanitizeProfile` is total and fills a missing one with its default.
 * Bumping for an additive change would delete every existing player's items to
 * buy nothing.
 */
export const parseProfile = (raw: string | null): ProfileLoadResult => {
  if (raw === null || raw === '') return { profile: defaultProfile(), status: 'missing' };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { profile: defaultProfile(), status: 'corrupt' };
  }
  if (!isRecord(parsed)) return { profile: defaultProfile(), status: 'corrupt' };
  if (parsed.version !== PROFILE_VERSION) {
    return { profile: defaultProfile(), status: 'stale' };
  }
  return { profile: sanitizeProfile(parsed), status: 'ok' };
};

/** Never throws, whatever storage does or whatever is stored. */
export const loadProfileResult = (
  storage: ProfileStorage | null = browserStorage()
): ProfileLoadResult => {
  if (!storage) return { profile: defaultProfile(), status: 'unavailable' };
  let raw: string | null;
  try {
    raw = readMigrating(storage, PROFILE_STORAGE_KEY, legacyKeyFor(PROFILE_STORAGE_KEY));
  } catch {
    // Safari in private mode used to throw on read; some extensions still do.
    return { profile: defaultProfile(), status: 'unavailable' };
  }
  return parseProfile(raw);
};

export const loadProfile = (storage: ProfileStorage | null = browserStorage()): PlayerProfile =>
  loadProfileResult(storage).profile;

export const serializeProfile = (p: PlayerProfile): string =>
  JSON.stringify(sanitizeProfile(p));

/** True if the write landed. A failed save is never worth an exception. */
export const saveProfile = (
  p: PlayerProfile,
  storage: ProfileStorage | null = browserStorage()
): boolean => {
  if (!storage) return false;
  try {
    storage.setItem(PROFILE_STORAGE_KEY, serializeProfile(p));
    return true;
  } catch {
    // QuotaExceededError, or storage disabled between boot and now.
    return false;
  }
};

export const clearProfile = (storage: ProfileStorage | null = browserStorage()): void => {
  if (!storage) return;
  try {
    storage.removeItem(PROFILE_STORAGE_KEY);
  } catch {
    /* nothing useful to do */
  }
};
